/**
 * Tenant communications log: list, record (with optional follow-up task), detail.
 */
import { COMMUNICATION_DIRECTIONS, COMMUNICATION_METHODS } from "@shared/constants.ts";
import { communicationSchema } from "@shared/schemas/operations.ts";
import { Card, Col, Row } from "react-bootstrap";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Attachments } from "../../components/Attachments.tsx";
import { AuditHistory } from "../../components/AuditHistory.tsx";
import { EntityForm } from "../../components/forms/EntityForm.tsx";
import {
  DateField,
  DateTimeField,
  SelectField,
  TextAreaField,
  TextField,
} from "../../components/forms/fields.tsx";
import { type Column, DataTable } from "../../components/ui/DataTable.tsx";
import { DateRangePicker } from "../../components/ui/DateRangePicker.tsx";
import { FilterBar, FilterSelect, SearchInput } from "../../components/ui/Filters.tsx";
import {
  Can,
  ConfirmButton,
  DefinitionList,
  EmptyState,
  IconButtonLink,
  PageHeader,
  QueryState,
  Section,
} from "../../components/ui/kit.tsx";
import { api, type Params, withQuery } from "../../lib/api.ts";
import { useFormat } from "../../lib/format.ts";
import { toOptions, useLookups } from "../../lib/lookups.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { useDateRange, useSortState, useUrlState } from "../../lib/url-state.ts";

interface CommunicationRow {
  id: string;
  propertyId: string | null;
  tenancyId: string | null;
  tenantId: string | null;
  tenantName: string | null;
  occurredAt: string;
  method: string;
  direction: string;
  subject: string;
  body: string;
  followUpTaskId: string | null;
  addressLine1: string | null;
  createdByName: string | null;
}

export function CommunicationTable({ filters, compact }: { filters: Params; compact?: boolean }) {
  const f = useFormat();
  const { sort, onSort } = useSortState("date", "desc");
  const q = useApi<CommunicationRow[]>("/api/communications", {
    ...filters,
    sort: sort.sort,
    dir: sort.dir,
    pageSize: compact ? 5 : 100,
  });
  const columns: Column<CommunicationRow>[] = [
    { key: "date", header: "When", sort: "date", cell: (c) => f.dateTime(c.occurredAt) },
    {
      key: "subject",
      header: "Subject",
      sort: "subject",
      cell: (c) => (
        <>
          <span className="fw-semibold">{c.subject}</span>
          {!compact && c.addressLine1 && <div className="small text-muted">{c.addressLine1}</div>}
        </>
      ),
    },
    ...(compact
      ? []
      : [
          { key: "tenant", header: "Tenant", cell: (c: CommunicationRow) => c.tenantName ?? "—" },
          {
            key: "method",
            header: "Method",
            cell: (c: CommunicationRow) => f.label(COMMUNICATION_METHODS, c.method),
          },
        ]),
    {
      key: "direction",
      header: "",
      cell: (c) => <span className="small text-muted">{c.direction === "inbound" ? "← In" : "Out →"}</span>,
    },
  ];
  return (
    <QueryState query={q}>
      {(rows) => (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(c) => c.id}
          rowLink={(c) => `/communications/${c.id}`}
          sort={sort}
          onSort={onSort}
          empty={
            compact ? (
              <p className="text-muted small p-3 mb-0">No communications logged.</p>
            ) : (
              <EmptyState
                icon="message"
                title="No communications logged"
                action={
                  <IconButtonLink
                    to={withQuery("/communications/new", { propertyId: filters.propertyId as string })}
                    icon="plus"
                  >
                    Log communication
                  </IconButtonLink>
                }
              >
                Keep a record of calls, emails, letters and visits with tenants.
              </EmptyState>
            )
          }
        />
      )}
    </QueryState>
  );
}

export function CommunicationListPage() {
  const range = useDateRange("last_12_months");
  const { get } = useUrlState();
  const lookups = useLookups();
  return (
    <>
      <PageHeader
        title="Communications"
        subtitle="Log of contact with tenants"
        actions={
          <Can permission="communications:write">
            <IconButtonLink to="/communications/new" icon="plus">
              Log communication
            </IconButtonLink>
          </Can>
        }
      />
      <Card>
        <FilterBar>
          <DateRangePicker range={range} />
          <SearchInput placeholder="Search subject, notes, tenant" />
          <FilterSelect param="propertyId" label="Property" options={toOptions(lookups?.properties)} />
          <FilterSelect param="tenantId" label="Tenant" options={toOptions(lookups?.tenants)} />
          <FilterSelect param="method" label="Method" options={COMMUNICATION_METHODS} />
        </FilterBar>
        <CommunicationTable
          filters={{
            from: range.from,
            to: range.to,
            propertyId: get("propertyId"),
            tenantId: get("tenantId"),
            method: get("method"),
            q: get("q"),
          }}
        />
      </Card>
    </>
  );
}

function localNow() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export function CommunicationFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const lookups = useLookups();
  return (
    <EntityForm
      schema={communicationSchema}
      resource="/api/communications"
      id={id}
      title={id ? "Edit communication" : "Log communication"}
      breadcrumbs={[{ label: "Communications", to: "/communications" }, { label: id ? "Edit" : "New" }]}
      defaults={{
        propertyId: params.get("propertyId") ?? "",
        tenancyId: params.get("tenancyId") ?? "",
        tenantId: params.get("tenantId") ?? "",
        occurredAt: localNow(),
        method: "phone",
        direction: "inbound",
        subject: "",
        body: "",
        followUpTitle: "",
        followUpDueDate: "",
      }}
      toForm={(r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v === null ? "" : v]))}
      redirect={(saved) => `/communications/${saved.id}`}
      cancelTo={id ? `/communications/${id}` : "/communications"}
    >
      {(form) => {
        const propertyId = form.watch("propertyId");
        return (
          <Row>
            <Col md={4}>
              <DateTimeField name="occurredAt" label="Date and time" required />
            </Col>
            <Col md={4}>
              <SelectField name="method" label="Method" options={COMMUNICATION_METHODS} required />
            </Col>
            <Col md={4}>
              <SelectField name="direction" label="Direction" options={COMMUNICATION_DIRECTIONS} required />
            </Col>
            <Col md={4}>
              <SelectField
                name="propertyId"
                label="Property"
                options={toOptions(lookups?.properties)}
                placeholder="—"
              />
            </Col>
            <Col md={4}>
              <SelectField
                name="tenancyId"
                label="Tenancy"
                options={toOptions(lookups?.tenancies, (t) => !propertyId || t.propertyId === propertyId)}
                placeholder="—"
              />
            </Col>
            <Col md={4}>
              <SelectField
                name="tenantId"
                label="Tenant"
                options={toOptions(lookups?.tenants)}
                placeholder="—"
              />
            </Col>
            <Col md={12}>
              <TextField name="subject" label="Subject" required placeholder="e.g. Reported leaking tap" />
              <TextAreaField name="body" label="Notes / content" rows={5} />
            </Col>
            {!id && (
              <>
                <Col md={8}>
                  <TextField
                    name="followUpTitle"
                    label="Create follow-up task (optional)"
                    placeholder="e.g. Book plumber"
                  />
                </Col>
                <Col md={4}>
                  <DateField name="followUpDueDate" label="Follow-up due" />
                </Col>
              </>
            )}
          </Row>
        );
      }}
    </EntityForm>
  );
}

export function CommunicationDetailPage() {
  const { id } = useParams();
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const q = useApi<CommunicationRow>(`/api/communications/${id}`);
  return (
    <QueryState query={q}>
      {(c) => (
        <>
          <PageHeader
            title={c.subject}
            subtitle={`${f.label(COMMUNICATION_DIRECTIONS, c.direction)} · ${f.label(COMMUNICATION_METHODS, c.method)} · ${f.dateTime(c.occurredAt)}`}
            breadcrumbs={[{ label: "Communications", to: "/communications" }, { label: c.subject }]}
            actions={
              <>
                <Can permission="communications:write">
                  <IconButtonLink to={`/communications/${c.id}/edit`} icon="edit" variant="outline-primary">
                    Edit
                  </IconButtonLink>
                </Can>
                <Can permission="communications:delete">
                  <ConfirmButton
                    size="md"
                    title="Delete this communication?"
                    body="The record will be removed from the log."
                    onConfirm={async () => {
                      await api.delete(`/api/communications/${c.id}`);
                      await invalidate();
                      navigate("/communications");
                    }}
                  >
                    Delete
                  </ConfirmButton>
                </Can>
              </>
            }
          />
          <Row>
            <Col lg={8}>
              <Section title="Details">
                <DefinitionList
                  items={[
                    [
                      "Tenant",
                      c.tenantId ? (
                        <Link key="t" to={`/tenants/${c.tenantId}`}>
                          {c.tenantName}
                        </Link>
                      ) : (
                        "—"
                      ),
                    ],
                    [
                      "Property",
                      c.propertyId ? (
                        <Link key="p" to={`/properties/${c.propertyId}`}>
                          {c.addressLine1}
                        </Link>
                      ) : (
                        "—"
                      ),
                    ],
                    [
                      "Tenancy",
                      c.tenancyId ? (
                        <Link key="tn" to={`/tenancies/${c.tenancyId}`}>
                          View tenancy
                        </Link>
                      ) : (
                        "—"
                      ),
                    ],
                    [
                      "Follow-up task",
                      c.followUpTaskId ? (
                        <Link key="f" to={`/tasks/${c.followUpTaskId}`}>
                          View task
                        </Link>
                      ) : (
                        "—"
                      ),
                    ],
                    ["Logged by", c.createdByName],
                  ]}
                />
                <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                  {c.body || <span className="text-muted">No notes.</span>}
                </p>
              </Section>
              <AuditHistory entityType="communication" entityId={c.id} />
            </Col>
            <Col lg={4}>
              <Attachments entityType="communication" entityId={c.id} title="Attachments" />
            </Col>
          </Row>
        </>
      )}
    </QueryState>
  );
}
